import axios from "axios";

interface ProductCompareResponse {
  id: string;
  status: number;
  data?: {
      status: 0|1|2,
      id: string|number,
      expire: string,
      data: Array<string|number>,
      count: number,
  };
}

interface CompareHandlerElement extends HTMLElement {
    _sitesmartCompareHandler?: (e: Event) => Promise<void>;
}

export default class Comparisons
{
    constructor()
    {
        this.init();
    }

    /**
     * NOTE: Generated by Claude Sonnet 4.5 from legacy code in sitesmart.js
     */
    init()
    {
        // Main event listener setup
        const siteContent: CompareHandlerElement|null = document.querySelector('.sitesmart-site-content');

        if (siteContent) {
            // Remove any existing listeners (if needed for hot reload scenarios)
            const existingHandler = siteContent._sitesmartCompareHandler;
            if (existingHandler) {
                siteContent.removeEventListener('click', existingHandler);
            }

            // Create and store the handler
            const clickHandler = async (e: Event) => {
                const target = e.target as HTMLElement;
                const addComparisonBtn = target.closest('.sitesmart-ecommerce-add-comparison');

                if (!addComparisonBtn) return;

                e.preventDefault();

                const id = addComparisonBtn.getAttribute('data-sitesmart-product-id');
                if (!id) return;

                const url = `${window._siteConfig.SITESMART_SITE_URL}@/Ecommerce/Compare?action=toggle&id=${id}&returnJson=1`;

                const combinedV3Compare = document.querySelector(
                    '.s-ec-combined-cart .s-ec-combined-cart__item--compare'
                );

                // Dispatch toggle event if combined compare exists
                if (combinedV3Compare) {
                    document.body.dispatchEvent(new CustomEvent('toggle-compare'));
                }

                try {
                    const response = await axios.get<ProductCompareResponse>(url);

                    const data = response?.data;
                    if (!data) return;

                    await handleCompareResponse(data, addComparisonBtn as HTMLElement, combinedV3Compare);

                } catch (error) {
                    console.error('Error toggling comparison:', error);
                }
            };

            // Store handler reference and attach
            siteContent._sitesmartCompareHandler = clickHandler;
            siteContent.addEventListener('click', clickHandler);
        }

        // Handle the comparison response
        async function handleCompareResponse(
            data: ProductCompareResponse,
            clickedElement: HTMLElement,
            combinedV3Compare: Element | null
        ): Promise<void> {

            const compareWidgets = document.querySelectorAll('.sitesmart-ecommerce-compare');
            const listButtonWrapper = document.querySelector(
                `.hover-overlay [data-sitesmart-product-id="${data.id}"]`
            );
            const textButtonWrapperAdd = document.querySelector(
                `.sitesmart-ecommerce-product-wrapper .add-comparison[data-sitesmart-product-id="${data.id}"]`
            ) as HTMLElement;
            const textButtonWrapperRemove = document.querySelector(
                `.sitesmart-ecommerce-product-wrapper .remove-comparison[data-sitesmart-product-id="${data.id}"]`
            ) as HTMLElement;

            let isRemove = false;

            // Dispatch completion event for combined compare
            if (combinedV3Compare) {
                document.body.dispatchEvent(new CustomEvent('toggle-compare-complete', {
                    detail: {
                        type: data.status === 2 ? 'remove' : 'add',
                        data: data
                    }
                }));
            }

            // Handle removal (status === 2)
            if (data.status === 2) {
                listButtonWrapper?.classList.remove('sitesmart-comparison-active');

                if (textButtonWrapperRemove) textButtonWrapperRemove.style.display = 'none';
                if (textButtonWrapperAdd) textButtonWrapperAdd.style.display = 'block';

                isRemove = true;

                // Handle comparison list removal
                const parentList = clickedElement.closest('.sitesmart-ecommerce-list');
                if (parentList?.getAttribute('data-sitesmart-comparison-list') === '1') {
                    const itemWrapper = clickedElement.closest('.item-wrapper');
                    const tableElement = document.getElementById(`sitesmart-ecommerce-comparison-table-${data.id}`);

                    await Promise.all([
                        fadeOutAndRemove(itemWrapper),
                        fadeOutAndRemove(tableElement)
                    ]);

                    location.reload();
                    return;
                }

                // Handle product item removal
                const productItem = document.querySelector(`.sitesmart-ecommerce-comparisons__productItem-${data.id}`);
                if (productItem) {
                    await fadeOutAndRemove(productItem);
                    location.reload();
                    return;
                }

            } else if (data.status === 1) {
                // Handle addition (status === 1)
                listButtonWrapper?.classList.add('sitesmart-comparison-active');

                if (textButtonWrapperAdd) textButtonWrapperAdd.style.display = 'none';
                if (textButtonWrapperRemove) textButtonWrapperRemove.style.display = 'block';
            }

            // Update compare widgets
            if (compareWidgets.length === 0) {
                // Handle combined cart v2
                const combinedV2 = document.querySelector('.sitesmartEcommerceCombined');

                if (combinedV2) {
                    const objectId = combinedV2.getAttribute('data-sitesmart-object-id');
                    if (objectId) {
                        window.SiteSmart_CartPopup.update(objectId);
                        await smoothScrollTo(combinedV2);
                    }
                    return;
                }
            }

            // Update each compare widget
            const updatePromises = Array.from(compareWidgets).map(async (widget) => {
                const widgetId = widget.id?.replace('sitesmart-ecommerce-comparison-', '');
                if (!widgetId) return;

                const compareObjectUrl = `${window._siteConfig.SITESMART_SITE_URL}@/Ecommerce/Compare/?action=getContent&objectId=${widgetId}`;

                try {
                    const response = await axios.get<string>(compareObjectUrl);
                    const content = response?.data;
                    if (!content) return;

                    const compareWrapper = 
                        document.querySelector(`.sitesmart-ecommerce-combined[data-sitesmart-object-id="${widgetId}"]`) ||
                        document.querySelector(`.sitesmart-ecommerce-comparison-wrapper-${widgetId}`);

                    if (compareWrapper) {
                        compareWrapper.outerHTML = content;

                        // Scroll to updated widget if not removing
                        if (!isRemove) {
                            const target = 
                                document.querySelector(`.sitesmart-ecommerce-combined[data-sitesmart-object-id="${widgetId}"]`) ||
                                document.querySelector(`.sitesmart-ecommerce-comparison-wrapper-${widgetId}`);

                            if (target) {
                                await smoothScrollTo(target);
                            }
                        }
                    }
                } catch (error) {
                    console.error(`Error updating compare widget ${widgetId}:`, error);
                }
            });

            await Promise.all(updatePromises);
        }

        // Utility: Smooth scroll to element
        function smoothScrollTo(element: Element): Promise<void> {
            return new Promise((resolve) => {
                const targetPosition = element.getBoundingClientRect().top + window.scrollY;

                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });

                // Wait for scroll to complete
                setTimeout(resolve, 500);
            });
        }

        // Utility: Fade out and remove element
        function fadeOutAndRemove(element: Element | null): Promise<void> {
            return new Promise((resolve) => {
                if (!element) {
                    resolve();
                    return;
                }

                const htmlElement = element as HTMLElement;
                htmlElement.style.transition = 'opacity 0.4s ease-out';
                htmlElement.style.opacity = '0';

                setTimeout(() => {
                    element.remove();
                    resolve();
                }, 400);
            });
        }
    }
}
